// React imports
import React, { useEffect, useRef, useState } from 'react';

// Import components
import { LoadingSpinner } from 'global/components/loading_ui/LoadingSpinnerAnimation';

// CSS import for this component
import './ImagePopup.css';

/**
 * ImagePopup
 *
 * A full-screen, accessible image viewer that overlays the page,
 * locks page scroll while open, traps initial focus on the close button,
 * and supports optional left and right navigation plus an optional side panel
 * for metadata or actions passed as children.
 *
 * Features:
 * - Closes with Escape or by clicking the dimmed background
 * - Prevents background scrolling while open
 * - Provides ARIA roles and labels for screen readers
 * - Optional navigation callbacks for previous and next images
 * - Optional side panel rendered via children
 */

/**
 * Example usage (simple — just an image with close button):
 *
 * ```jsx
 * const [focusImage, setFocusImage] = useState(null);
 *
 * <button onClick={() => setFocusImage({ image_url: '/images/dog.jpg' })}>
 *   View dog photo
 * </button>
 *
 * <ImagePopup
 *   focusImage={focusImage}
 *   alt="Happy dog in the park"
 *   onClose={() => setFocusImage(null)}
 * />
 * ```
 *
 * Example usage (complex — with navigation buttons and side panel):
 *
 * ```jsx
 * const [focusImage, setFocusImage] = useState(null);
 *
 * <button onClick={() => setFocusImage({ image_url: '/images/cat.jpg' })}>
 *   Open image
 * </button>
 *
 * <ImagePopup
 *   focusImage={focusImage}
 *   alt="A very relaxed cat, close-up"
 *   onClose={() => setFocusImage(null)}
 *   onLeft={() => console.log('Go to previous')}
 *   onRight={() => console.log('Go to next')}
 * >
 *   <div>
 *     <h3 className="text-lg">Cat details</h3>
 *     <p>Taken with a 50mm lens in natural light.</p>
 *   </div>
 * </ImagePopup>
 * ```
 */

// Exported component
const ImagePopup = ({

  // The focused image object for display, expects an { image_url } shape; falsy means closed
  focusImage,

  // Descriptive alt text for the main image, improves accessibility
  alt = 'Full size image',

  // Called to close the popup, used by Escape key, overlay click, and close button
  onClose,

  // Optional: called when user requests the previous image
  onLeft,

  // Optional: called when user requests the next image
  onRight,

  // Optional: side panel content, such as metadata or actions
  children,

  // Optional: custom main media content (used when there is no final image to render)
  mediaContent,

}) => {

  // Reference to the outer overlay to detect background clicks for closing
  const overlayRef = useRef(null);

  // Reference to the close button so we can place initial focus for keyboard users
  const closeBtnRef = useRef(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const focusImageUrl = focusImage?.image_url || '';

  // Handle lifecycle while the popup is open: focus, Escape to close, and page scroll lock
  useEffect(() => {

    // If there is no image to show, do nothing
    if (!focusImage) {

      return;

    }

    // Handle Escape key to close the viewer
    const handleKeyDown = (e) => {

      if (e.key === 'Escape') {

        onClose?.();

      }

    };

    // Remember current body overflow and then prevent background scrolling
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    // Add the keyboard listener
    window.addEventListener('keydown', handleKeyDown);

    // Move focus to the close button to give keyboard users a clear starting point
    closeBtnRef.current?.focus();

    // Restore previous state and remove listeners when closed or unmounted
    return () => {

      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;

    };

  }, [focusImage, onClose]);

  useEffect(() => {

    if (!focusImageUrl || mediaContent) {

      setIsImageLoading(false);

      return;

    }

    setIsImageLoading(true);

  }, [focusImageUrl, mediaContent]);

  // If there is no focused image, render nothing
  if (!focusImage) {

    return null;

  }

  // Clicking on the overlay (but not on the content) should close the popup
  const handleOverlayClick = (e) => {

    if (e.target === overlayRef.current) {

      onClose?.();

    }

  };

  // Render the overlayed dialog
  return (

    <div
      // this is a modal dialog focused on viewing an image
      className="image-popup__overlay"
      ref={overlayRef}
      onMouseDown={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >

      <div
        // Stop event propagation so clicks inside content do not trigger overlay close
        className="image-popup__content"
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >

        {/* Standard close control at modal top-right */}
        <button
          type="button"
          className="image-popup__close popup-close"
          aria-label="Close image"
          onClick={onClose}
          ref={closeBtnRef}
        >
          X
        </button>

        {/* 
          Main media area.
          Apply a modifier class when a side panel is present to adjust layout.
        */}
        <div
          className={
            [
              'image-popup__media',
              children ? 'image-popup__media--with-panel' : '',
              mediaContent ? 'image-popup__media--custom' : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
        >

          {/* Optional left navigation control when a callback is provided */}
          {onLeft && (

            <button
              type="button"
              className="image-popup__nav image-popup__nav--left"
              aria-label="Previous image"
              onClick={onLeft}
            >
              &#9666;
            </button>

          )}

          {/* Optional right navigation control when a callback is provided */}
          {onRight && (

            <button
              type="button"
              className="image-popup__nav image-popup__nav--right"
              aria-label="Next image"
              onClick={onRight}
            >
              &#9656;
            </button>

          )}

          {mediaContent ? (

            <div className="image-popup__media-custom">

              {mediaContent}

            </div>

          ) : (

            <div className="image-popup__image-frame">

              {isImageLoading && (

                <div className="image-popup__loading" role="status" aria-live="polite" aria-label="Loading image">

                  <LoadingSpinner />

                </div>

              )}

              <img
                key={focusImageUrl}
                src={focusImageUrl}
                alt={alt}
                className={`image-popup__image ${isImageLoading ? 'image-popup__image--loading' : ''}`}
                draggable={false}
                onLoad={() => {
                  setIsImageLoading(false);
                }}
                onError={() => {
                  setIsImageLoading(false);
                }}
              />

            </div>

          )}

        </div>

        {/* Optional side panel for metadata, actions, or captions */}
        {children && (

          <aside className="image-popup__panel">

            <div className="image-popup__panel-inner">

              {children}

            </div>

          </aside>
          
        )}

      </div>

    </div>

  );
  
};

// Default export for convenient importing
export default ImagePopup;
